import { useState } from 'react'
import { SEO } from '@/components/seo'
import { Button } from '@/components/ui'
import { painPoints, features, documentTemplates, steps } from '@/data/mortgage'
import type { MortgagePainPoint, MortgageFeature } from '@/data/mortgage'
import FeatureWalkthroughModal from '@/components/FeatureWalkthroughModal'

// Pain point icon components
function PainPointIcon({ type }: { type: MortgagePainPoint['icon'] }) {
  const props = { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: '#3b82f6', strokeWidth: 1.5 }
  switch (type) {
    case 'papers':
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      )
    case 'clock':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      )
    case 'shield':
      return (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      )
    case 'notes':
      return (
        <svg {...props}>
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      )
  }
}

// Feature icon components
function FeatureIcon({ type }: { type: MortgageFeature['icon'] }) {
  const props = { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: '#3b82f6', strokeWidth: 1.5 }
  switch (type) {
    case 'mic':
      return (
        <svg {...props}>
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      )
    case 'ramble':
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <line x1="9" y1="9" x2="15" y2="9" />
          <line x1="9" y1="13" x2="13" y2="13" />
        </svg>
      )
    case 'document':
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M12 18v-6" />
          <path d="M9 15l3 3 3-3" />
        </svg>
      )
    case 'workflow':
      return (
        <svg {...props}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      )
  }
}

// Step number colours
const stepColors = [
  'bg-gradient-to-br from-[#3b82f6] to-[#2563eb]',
  'bg-gradient-to-br from-[#14b8a6] to-[#0d9488]',
  'bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed]',
  'bg-gradient-to-br from-[#f59e0b] to-[#d97706]',
]

export default function MortgageAdvisersPage() {
  const [selectedFeature, setSelectedFeature] = useState<MortgageFeature | null>(null)

  return (
    <>
      <SEO
        title="For Mortgage Advisers - WealthSpace | Less Admin, Better Outcomes"
        description="WealthSpace helps mortgage advisers reduce admin, generate suitability letters, capture meeting notes, and streamline compliance documentation with AI."
        keywords="mortgage adviser software, mortgage broker software, suitability letter generation, mortgage compliance, meeting transcription, AI mortgage documents"
        url="/mortgage-advisers"
      />

      {/* Feature Walkthrough Modal */}
      {selectedFeature && (
        <FeatureWalkthroughModal
          title={selectedFeature.title}
          icon={selectedFeature.icon}
          steps={selectedFeature.walkthroughSteps}
          accentColor="#3b82f6"
          accentGradient="linear-gradient(135deg, #3b82f6, #2563eb)"
          onClose={() => setSelectedFeature(null)}
        />
      )}

      {/* Hero Section */}
      <section className="pt-[100px] pb-20 px-6 max-w-[1200px] mx-auto relative z-[1] text-center">
        <p className="text-sm font-semibold text-[#3b82f6] tracking-[0.1em] uppercase mb-6">
          For Mortgage Advisers
        </p>
        <h1 className="font-serif text-[36px] sm:text-[clamp(42px,6vw,60px)] font-normal leading-[1.15] tracking-[-0.02em] mb-6 max-w-[900px] mx-auto">
          Less admin. Better outcomes.{' '}
          <span className="text-[#3b82f6]">Welcome to WealthSpace.</span>
        </h1>
        <p className="text-[19px] text-brand-muted leading-[1.7] font-normal max-w-[650px] mx-auto mb-10">
          AI-powered software that transforms how mortgage advisers work. From consultation to
          completion, WealthSpace handles the documentation so you can focus on finding the right
          deal for your clients.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button href="/book-demo">
            Book a Demo
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
          <a
            href="#features"
            className="px-9 py-[18px] rounded-md font-semibold text-base inline-flex items-center justify-center gap-2 transition-all duration-300 bg-white/5 text-brand-light border border-white/15 hover:bg-white/10 hover:border-white/25 no-underline"
          >
            See Features
          </a>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1] bg-gradient-to-br from-[rgba(59,130,246,0.05)] to-transparent border-t border-b border-[rgba(59,130,246,0.1)]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-brand-light text-center mb-4">
            Sound familiar?
          </h2>
          <p className="text-lg text-brand-muted text-center max-w-[600px] mx-auto mb-10 sm:mb-[60px]">
            The daily reality of mortgage advice admin
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {painPoints.map((point) => (
              <div
                key={point.title}
                className="p-8 bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl transition-all duration-300 hover:border-[rgba(59,130,246,0.3)] hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[rgba(59,130,246,0.2)] to-[rgba(59,130,246,0.1)] flex items-center justify-center mb-5">
                  <PainPointIcon type={point.icon} />
                </div>
                <h3 className="text-lg font-semibold text-brand-light mb-3">{point.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How WealthSpace Helps (Features) */}
      <section id="features" className="py-16 sm:py-[100px] px-6 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-brand-light text-center mb-4">
            How WealthSpace helps
          </h2>
          <p className="text-lg text-brand-muted text-center max-w-[600px] mx-auto mb-10 sm:mb-[60px]">
            Core capabilities built for mortgage advice workflows
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((feature) => (
              <button
                key={feature.title}
                onClick={() => setSelectedFeature(feature)}
                className="group text-left p-8 bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl transition-all duration-300 hover:border-[rgba(59,130,246,0.3)] hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[rgba(59,130,246,0.2)] to-[rgba(59,130,246,0.1)] flex items-center justify-center mb-5">
                  <FeatureIcon type={feature.icon} />
                </div>
                <h3 className="text-lg font-semibold text-brand-light mb-3">{feature.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed mb-4">{feature.description}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3b82f6] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  See how it works
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Document Templates Section */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1] bg-gradient-to-br from-[rgba(59,130,246,0.05)] to-transparent border-t border-b border-[rgba(59,130,246,0.1)]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-brand-light text-center mb-4">
            Documents WealthSpace can generate
          </h2>
          <p className="text-lg text-brand-muted text-center max-w-[600px] mx-auto mb-10 sm:mb-[60px]">
            AI-powered templates tailored to mortgage advice
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentTemplates.map((template) => (
              <div
                key={template.title}
                className="flex items-center gap-3 p-5 bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-xl transition-all duration-300 hover:border-[rgba(59,130,246,0.3)]"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span className="text-sm text-brand-light font-medium">{template.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-brand-light text-center mb-4">
            How it works
          </h2>
          <p className="text-lg text-brand-muted text-center max-w-[600px] mx-auto mb-10 sm:mb-[60px]">
            Four simple steps to less admin
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="text-center p-8 bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl transition-all duration-300 hover:border-[rgba(59,130,246,0.3)] hover:-translate-y-1"
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold text-white ${stepColors[index]}`}
                >
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold text-brand-light mb-3">{step.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-[120px] px-6 max-w-[900px] mx-auto relative z-[1]">
        <div className="bg-gradient-to-br from-[rgba(59,130,246,0.1)] to-[rgba(59,130,246,0.02)] border border-[rgba(59,130,246,0.2)] rounded-3xl py-12 sm:py-20 px-8 sm:px-12 text-center">
          <h2 className="font-serif text-[32px] sm:text-[44px] font-normal text-brand-light tracking-[-0.02em] mb-5">
            Ready to reduce your admin?
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[500px] mx-auto mb-10">
            See how WealthSpace can transform the way you advise on mortgages.
          </p>
          <Button href="/book-demo" size="lg">
            Book a Demo
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
          <p className="text-sm text-brand-subtle mt-6">Free. Friendly. No obligation.</p>
        </div>
      </section>
    </>
  )
}
